import { FlexView, Spacer, TextFormatter, Alert, FieldFormatter, Image } from '@facile-it/shark-morph/components'
import * as O from 'fp-ts/Option'
import { pipe } from 'fp-ts/function'
import * as React from 'react'
import { ajax } from 'rxjs/ajax'
import { ConfigurationProvider } from '../../../../../../../config'
import { urlSearcherParamsMatcher } from '../../../../../../../shark-lib/ts/services/Routing/urlSearcherParamsMatcher'
import type { Request } from '../../../../../../../shark-lib/ts/types/Request'
import { FormProspect } from './formProspect'
import { SubmitFeedback } from './submitFeedback'

declare global {
    interface Window {
        dataLayer: {
            push: (payload: Record<string, unknown>) => void
        }
    }
}

export const COUPON_REGEX = /^BROCHURE$|^COLLABORATORE|^FACILE$|^[A-Z]{5}/i
export const FORM_VALIDATOR_REGEX = /^BROCHURE$|^COLLABORATORE|^FACILE$|^[A-Z]{5}$/i

export const BecomePartner: React.FC = () => {
    const [requestForm, setRequestForm] = React.useState<Request<unknown>>({ status: 'init' })

    function getCouponFromQueryString(): O.Option<string> {
        return pipe(
            urlSearcherParamsMatcher<{ coupon: string }>([{ from: 'coupon', to: 'coupon', test: COUPON_REGEX }]),
            O.fromNullable,
            O.chainNullableK(r => r.coupon),
            O.filter(c => typeof c === 'string')
        )
    }

    function renderForm() {
        switch (requestForm.status) {
            case 'init':
            case 'submitted':
            case 'rejected':
                if (requestForm.status === 'rejected' && requestForm.statusCode === 409) {
                    return (
                        <SubmitFeedback title="Numero RUI già presente!">
                            <TextFormatter fontFamily="alt-lg" fontSize="xl" textAlign="center">
                                Sembra che ci sia già una collaborazione attiva.
                            </TextFormatter>
                            <TextFormatter fontFamily="alt-lg" fontSize="xl" textAlign="center" borderless>
                                Per saperne di più invia una mail ad{' '}
                                <FieldFormatter.Color color="primary">attivazioni@facile.it</FieldFormatter.Color>
                            </TextFormatter>
                        </SubmitFeedback>
                    )
                }

                return (
                    <FormProspect
                        onSubmitProspect={data => {
                            setRequestForm({ status: 'submitted' })

                            ajax.post(
                                `${ConfigurationProvider.getConfiguration().basePublicPath}api/public/prospect`,
                                data
                            ).subscribe(
                                xhr => {
                                    setRequestForm({
                                        status: 'fulfilled',
                                        statusCode: xhr.response.statusCode,
                                        data: xhr.response.data,
                                    })

                                    if (window.dataLayer && window.dataLayer.push) {
                                        window.dataLayer.push({
                                            event: 'lead-buonfine',
                                            RUI: data.ruiCode,
                                        })
                                    }
                                },
                                (e: unknown) => {
                                    const error: { status: number; response?: { data: unknown } } = e as any

                                    if (error.status >= 400 && error.status < 500) {
                                        setRequestForm({
                                            status: 'rejected',
                                            statusCode: error.status,
                                            data: error.response ? error.response.data : null,
                                        })
                                    } else {
                                        setRequestForm({
                                            status: 'fail',
                                            statusCode: error.status,
                                            data: error.response ? error.response.data : null,
                                        })
                                    }
                                }
                            )
                        }}
                        coupon={getCouponFromQueryString()}
                        postResource={requestForm}
                    />
                )
            case 'fulfilled':
                return (
                    <SubmitFeedback title="Fatto!">
                        <TextFormatter fontFamily="alt-lg" textAlign="center" fontSize="xl">
                            Hai inviato correttamente la tua candidatura. A breve verrai contattato da un nostro
                            consulente per ricevere maggiori informazioni.
                        </TextFormatter>
                    </SubmitFeedback>
                )
            case 'fail':
                return (
                    <Alert type="danger">
                        <TextFormatter fontFamily="alt-lg" borderless>
                            Servizio attualmente non disponibile. Riprova più tardi.
                        </TextFormatter>
                    </Alert>
                )
        }
    }

    return (
        <div className="become-partner">
            <TextFormatter fontColor="black" fontWeight="bold" fontSize="xl" fontFamily="alt-lg" borderless>
                Diventa anche tu un Facile.it Partner!
            </TextFormatter>
            <Spacer height={0.9} />
            <TextFormatter fontFamily="alt-lg" borderless>
                Compila il modulo per inviare una richiesta di collaborazione e attendi la chiamata di un nostro
                referente commerciale per approfondire.
            </TextFormatter>
            <Spacer height={0.9} />
            <FlexView gutters={{ r: { xs: 'none', lg: 'md' }, tb: { xs: 'std' } }}>
                <FlexView.Item xs={12} lg={5}>
                    {renderForm()}
                </FlexView.Item>
                <FlexView.Item xs={12} lgOffset={1} lg={5.5}>
                    <Image margin="none" src={require('@images/people.jpeg')} />
                </FlexView.Item>
            </FlexView>
        </div>
    )
}
